import React, { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import axios from "axios";
import { motion } from "framer-motion";

import "./style.css";
import utils from "../../utils/utils";
import { appendFormData } from "../../helper";
import { _setAiResponse } from "../../redux/actions/authAction";
import LogOut from "../../components/logout/LogOut";
import { useSubscriptionGuard } from "../../hooks/subscription-hook/UseSubscriptionGuard";

const AiScreen = () => {
  useSubscriptionGuard();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();

  const handleFile = (selectedFile) => {
    if (!selectedFile) return;
    setFile(selectedFile);
    setPreview(URL.createObjectURL(selectedFile));
  };

  const handlePickFile = (e) => {
    if (e.target.files.length) {
      handleFile(e.target.files[0]);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.files.length) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const startUsingAI = async () => {
    if (!file) return;
    try {
      setLoading(true);

      const payload = { pass_key: "KW1555ZWRFFFRTTKW" };
      const headers = { "content-type": "multipart/form-data" };
      const upload = appendFormData("file", file, payload);

      const response = await axios.post(
        "https://analysis-api.dentalid.app",
        upload,
        { headers }
      );

      if (
        response.data.status === "Failure" ||
        response.data.StatusCode === 422
      ) {
        alert("Invalid Image");
        setLoading(false);
        return;
      }

      dispatch(_setAiResponse(response?.data));
      navigate("/ai-measurement/result");
    } catch (error) {
      console.error("AI Analysis error:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ai-screen">
      {/* Header */}
      <div className="ai-header">
        <div className="ai-header-left">
          <Link to="/">
            <img src={utils.images.applogo} alt="logo" className="ai-logo" />
          </Link>
          <span className="ai-title">Second Opinion</span>
        </div>
        <div className="ai-header-actions">
          {file && (
            <button
              className="ai-btn-analyze"
              onClick={startUsingAI}
              disabled={loading}
            >
              {loading ? "Analyzing..." : "AI Analysis"}
            </button>
          )}
          <LogOut />
        </div>
      </div>

      {/* Body */}
      <div
        className={`ai-body ${file ? "with-preview" : ""}`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
      >
        {!file && !loading && (
          <motion.div
            className="ai-dropzone"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <svg
              className="ai-dropzone-icon"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M7 16a4 4 0 01-.88-7.903A5.002 5.002 0 0115.9 6H16a5 5 0 011 9.9M12 12v9m0-9l-3 3m3-3l3 3"
              />
            </svg>
            <h3>Upload Radiograph</h3>
            <p>Drag & Drop your file here</p>
            <p className="or">or</p>
            <button
              className="ai-btn-upload"
              onClick={() => fileInputRef.current.click()}
            >
              Select Image
            </button>
            <input
              type="file"
              hidden
              ref={fileInputRef}
              onChange={handlePickFile}
              accept="image/*"
            />
          </motion.div>
        )}

        {preview && !loading && (
          <motion.div
            className="ai-preview"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <img src={preview} alt="preview" />
          </motion.div>
        )}

        {loading && preview && (
          <motion.div
            className="ai-loading-image"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <div className="ai-scan-wrapper">
              <img src={preview} alt="preview" className="ai-loading-preview" />
              <div className="ai-scan-line"></div>
            </div>
            <p className="ai-status-text">
              Running AI Image Analysis<span className="loading-dots"></span>
            </p>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default AiScreen;
